import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';

/**
 * Personal Summary Task-Redirect Guard
 *
 * Restores a UI-Router behavior the Angular Router migration silently dropped.
 *
 * In UI-Router two states shared the base url `/personalSummary`, disambiguated ONLY
 * by the presence of a query param:
 *   - `dashboard.policyTaskRedirect`  url `/personalSummary?taskId`  -> TaskRedirectComponent
 *   - `dashboard.personalSummary`     url `/personalSummary`         -> PersonalSummaryComponent
 *
 * Angular Router matches on path segments and ignores query params, so it cannot
 * disambiguate two routes by "does ?taskId exist". The migration gave the states distinct
 * paths (`personalSummary` vs `policyTaskRedirect`), but external links — notably the
 * notification/alert deep links built by the header app (sircon-header-ui
 * alert.service.ts: `.../#/dashboard/personalSummary?taskId=<id>`) — still point at the
 * old shared url. Those now land on PersonalSummaryComponent directly, bypassing the task
 * processing (getById -> read subjectPartyAccountType -> route to the correct destination),
 * so the user sees an empty page with no applicable tabs.
 *
 * This guard re-links the old url: when `personalSummary` is entered WITH a `taskId`, it
 * forwards to `/dashboard/policyTaskRedirect` preserving all query params. Without a
 * `taskId` (e.g. ?Scheduler=true or a plain visit) it allows PersonalSummaryComponent
 * through unchanged.
 *
 * Ordered AFTER EnhancedAuthGuard on the route so authentication/login redirect still
 * runs first; policyTaskRedirect carries its own auth guard.
 *
 * See docs/routing/QUERY_PARAM_SERIALIZATION_REGRESSIONS.md.
 */
@Injectable({
    providedIn: 'root'
})
export class PersonalSummaryTaskRedirectGuard implements CanActivate {
    constructor(private router: Router) {}

    canActivate(route: ActivatedRouteSnapshot): boolean | UrlTree {
        const taskId = route.queryParams['taskId'];
        const requestedCarrierNetworkId = route.queryParams['requestedCarrierNetworkId'];
        const activeTabIndex = route.queryParams['activeTabIndex'];

        // Only redirect to policyTaskRedirect if we have taskId but NOT the full E&O context.
        // If we have taskId + requestedCarrierNetworkId + activeTabIndex, it means we're
        // coming FROM task-redirect with the full context already resolved, so allow through.
        if (taskId && !requestedCarrierNetworkId && !activeTabIndex) {
            // Forward to the task-redirect route, preserving every query param the deep
            // link carried (taskId plus any subType/requestedCarrierNetworkId/etc.).
            return this.router.createUrlTree(['/dashboard/policyTaskRedirect'], {
                queryParams: route.queryParams
            });
        }
        return true;
    }
}
